import { Flex, LoadingOverlay, Select, Text } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { StatItem } from '../components/StatItem';
import { Bar, Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  BarElement,
  PointElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  LineController,
  BarController,
} from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import zoomPlugin from 'chartjs-plugin-zoom';
import { IconBarbellFilled, IconBike, IconWeight, IconDeviceMobileRotated } from '@tabler/icons-react';
import colors from '../utils/colors';
import { useStats } from '../hooks/useStats';
import { useSumStats } from '../hooks/useSumStats';
import { useUserMachines } from '../hooks/useUserMachines';
import { useDashboard } from '../hooks/useDashboard';
import { useLoadingOverlay } from '../components/LoadingOverlayContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { staggerContainer, fadeInFromLeft, fadeInFromRight, fadeInFromBottom, fadeIn } from '../utils/animations';
import { getMachineOptions } from '../utils/machineTranslations';

ChartJS.register(CategoryScale, LinearScale, LineElement, BarElement, PointElement, ChartTitle, Tooltip, Legend, LineController, BarController, ChartDataLabels, zoomPlugin);

const allMachineTypes = getMachineOptions();

const isCardio = (machineType: any): boolean => {
  return machineType === 'ERGOMETR' ||
         machineType === 'BASIC_ERGOMETR' ||
         machineType === 'CROSSWALKER' ||
         machineType === 'BASIC_CROSSWALKER';
};

const Stats: React.FC = () => {
  const { translate } = useLanguage();
  const [chartType, setChartType] = useState<"line" | "bar">("line");
  const [selectedMachine, setSelectedMachine] = useState<string>("");
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>("1");
  const { setLoading } = useLoadingOverlay();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [visibleDatasets, setVisibleDatasets] = useState({ reps: true, kg: true });
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const timeRangeOptions = [
    { value: "1", label: translate('lastMonth') },
    { value: "3", label: translate('last3Months') },
    { value: "6", label: translate('last6Months') },
    { value: "12", label: translate('lastYear') },
  ];

  // Fetch user's available machines
  const { data: userMachinesData, isLoading: userMachinesLoading, error: userMachinesError } = useUserMachines();
  const { data: dashboardData } = useDashboard();
  
  // Filter all machine types to only show user's machines
  const availableMachineTypes = allMachineTypes.filter(machine => 
    userMachinesData?.machineTypes.includes(machine.value)
  );

  const groupedMachineTypes = [
    {
      group: "ALFA",
      items: availableMachineTypes.filter(item => !item.value.includes("BASIC"))
    },
    {
      group: "BASIC",
      items: availableMachineTypes.filter(item => item.value.includes("BASIC"))
    }
  ];
  
  // Set default machine when user machines are loaded
  useEffect(() => {
    if (userMachinesData?.machineTypes.length && !selectedMachine) {
      const firstAvailableMachine = availableMachineTypes[0];
      if (firstAvailableMachine) {
        setSelectedMachine(firstAvailableMachine.value);
      }
    }
  }, [userMachinesData, selectedMachine, availableMachineTypes]);
  
  // Only fetch stats when a machine is selected
  const { data: statsData, isLoading: statsLoading, error: statsError } = useStats(selectedMachine);
  const { data: sumStatsData, isLoading: sumStatsLoading, error: sumStatsError } = useSumStats(selectedMachine, parseInt(selectedTimeRange, 10));

  useEffect(() => {
    setLoading(statsLoading || sumStatsLoading || userMachinesLoading);
    
    if (!statsLoading && !sumStatsLoading && !userMachinesLoading && statsData && sumStatsData && userMachinesData) {
      setTimeout(() => setShouldAnimate(true), 50);
    }
  }, [statsLoading, sumStatsLoading, userMachinesLoading, statsData, sumStatsData, userMachinesData, setLoading]);

  const now = new Date();
  const globalStartDate = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());

  const displayMonths = parseInt(selectedTimeRange, 10);
  const displayStartDate = new Date(now.getFullYear(), now.getMonth() - displayMonths, now.getDate());

  const stats = statsData?.loadInTime || [];
  const sortedStats = [...stats].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  
  const completeDatesFull: Date[] = [];
  for (let d = new Date(globalStartDate); d <= now; d.setDate(d.getDate() + 1)) {
    completeDatesFull.push(new Date(d));
  }

  const displayDates = completeDatesFull.filter(date => date >= displayStartDate);
  const completeLabels = displayDates.map(date => date.toLocaleDateString());

  let currentValue1 = 0
  let currentValue2 = 0
  let statIndex = 0;
  const completeData1: number[] = [];
  const completeData2: number[] = [];
  const completeRepsData: (number | null)[] = [];
  const completeKgData: (number | null)[] = [];
  const isCardioMachine = isCardio(selectedMachine);

  function dateOnly(d: Date | string): Date {
    const date = new Date(d);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
  
  for (const day of completeDatesFull) {
    let repsForDay: number | null = null;
    let kgForDay: number | null = null;
    let totalRepsForDay = 0;
    let totalKgForDay = 0;
    let hasRepsData = false;
    let hasKgData = false;
    
    while (
      statIndex < sortedStats.length &&
      dateOnly(sortedStats[statIndex].date).getTime() <= dateOnly(day).getTime()
    ) {
      if (isCardioMachine) {
        currentValue1 = sortedStats[statIndex].nm ?? currentValue1;
        currentValue2 = sortedStats[statIndex].w ?? currentValue2;
      } else {
        currentValue1 = sortedStats[statIndex].concentric ?? currentValue1;
        currentValue2 = sortedStats[statIndex].eccentric ?? currentValue2;
      }
      
      // Sum all reps and kg for the exact date, not interpolated
      if (dateOnly(sortedStats[statIndex].date).getTime() === dateOnly(day).getTime()) {
        const repsValue = sortedStats[statIndex].reps;
        const kgValue = sortedStats[statIndex].kg;
        
        if (repsValue !== null) {
          totalRepsForDay += repsValue;
          hasRepsData = true;
        }
        
        if (kgValue !== null && !isCardioMachine) {
          totalKgForDay += kgValue;
          hasKgData = true;
        }
      }
      
      statIndex++;
    }
    
    repsForDay = hasRepsData ? totalRepsForDay : null;
    kgForDay = hasKgData ? totalKgForDay : null;
    
    completeData1.push(currentValue1);
    completeData2.push(currentValue2);
    completeRepsData.push(repsForDay);
    completeKgData.push(kgForDay);
  }

  const displayData1 = completeData1.filter((_, idx) => completeDatesFull[idx] >= displayStartDate);
  const displayData2 = completeData2.filter((_, idx) => completeDatesFull[idx] >= displayStartDate);
  const displayRepsData = completeRepsData.filter((_, idx) => completeDatesFull[idx] >= displayStartDate);
  const displayKgData = completeKgData.filter((_, idx) => completeDatesFull[idx] >= displayStartDate);
  
  // Calculate max for first chart (concentric/eccentric) with breathing room
  const maxData1 = displayData1.length > 0 ? Math.max(...displayData1) : 0;
  const maxData2 = displayData2.length > 0 ? Math.max(...displayData2) : 0;
  const maxFirstChart = Math.max(maxData1, maxData2);
  const firstChartMax = maxFirstChart > 0 ? Math.ceil((maxFirstChart * 1.1) / 5) * 5 : 20;
  
  // Calculate dynamic max for reps axis
  const validRepsData = displayRepsData.filter(reps => reps !== null) as number[];
  const maxRepsValue = validRepsData.length > 0 ? Math.max(...validRepsData) : 0;
  const repsAxisMax = maxRepsValue > 40 ? Math.ceil(maxRepsValue / 10) * 10 : 40;

  // Calculate dynamic max based on visible datasets for second chart
  const validKgData = displayKgData.filter(kg => kg !== null) as number[];
  const maxKgValue = validKgData.length > 0 ? Math.max(...validKgData) : 0;
  
  let dynamicAxisMax = 50;
  if (visibleDatasets.reps && visibleDatasets.kg) {
    // Both visible - round to multiples of 25 to match step size
    const combinedMax = Math.max(maxKgValue, maxRepsValue);
    dynamicAxisMax = Math.max(50, Math.ceil((combinedMax + 10) / 25) * 25);
  } else if (visibleDatasets.kg && !visibleDatasets.reps) {
    // Only KG visible - round to multiples of 25 to match step size
    dynamicAxisMax = Math.max(50, Math.ceil((maxKgValue + 10) / 25) * 25);
  } else if (visibleDatasets.reps && !visibleDatasets.kg) {
    // Only reps visible - round to multiples of 5 to match step size
    dynamicAxisMax = Math.max(20, Math.ceil((maxRepsValue + 2) / 5) * 5);
  }
  
  const kgAxisMax = dynamicAxisMax;

  const dataset1Label = isCardioMachine ? "Nm" : translate('concentric');
  const dataset2Label = isCardioMachine ? "W" : translate('eccentric');

  const actualDatesSet = new Set(stats.map(item => new Date(item.date).toLocaleDateString()));

  const pointRadius = completeLabels.map(label =>
    actualDatesSet.has(label) ? 5 : 1
  );
  const barColors1 = completeLabels.map(label =>
    actualDatesSet.has(label) ? '#00b7ff' : '#00b7ff30'
  );
  const barColors2 = completeLabels.map(label =>
    actualDatesSet.has(label) ? '#ff5100' : '#ff510030'
  );

  const dataChartLine = {
    labels: completeLabels,
    datasets: [
      {
        label: dataset1Label,
        data: displayData1,
        borderColor: '#00b7ff',
        backgroundColor: '#00b7ff',
        pointRadius: pointRadius,
        fill: false,
        tension: 0.1,
        yAxisID: 'y',
      },
      {
        label: dataset2Label,
        data: displayData2,
        borderColor: '#ff5100',
        backgroundColor: '#ff5100',
        pointRadius: pointRadius,
        fill: false,
        tension: 0.1,
        yAxisID: 'y',
      },
    ]
  };

  const dataChartBar = {
    labels: completeLabels,
    datasets: [
      {
        label: dataset1Label,
        data: displayData1,
        borderColor: '#00b7ff',
        backgroundColor: barColors1,
        fill: false,
        tension: 0.1,
        yAxisID: 'y',
      },
      {
        label: dataset2Label,
        data: displayData2,
        borderColor: '#ff5100',
        backgroundColor: barColors2,
        fill: false,
        tension: 0.1,
        yAxisID: 'y',
      },
    ]
  };

  const defaultZoomDays = isPortrait ? 21 : 28;
  const zoomEndIndex = completeLabels.length - 1;
  const zoomStartIndex = Math.max(0, zoomEndIndex - defaultZoomDays);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const },
      datalabels: {
        display: false,
      },
      zoom: {
        limits: {
          x: { min: 0, max: completeLabels.length - 1 }
        },
        pan: {
          enabled: true,
          mode: 'x' as const,
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'x' as const,
        }
      }
    },
    scales: {
      x: { 
        title: { display: false, text: translate('date') },
        min: zoomStartIndex,
        max: zoomEndIndex,
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: { display: true, text: isCardio(selectedMachine) ? translate('load') : translate('load') + ' [kg]' },
        ticks: { 
          stepSize: 5,
          callback: function(value: any) {
            return value.toString().padStart(3, ' ');
          }
        },
        min: 0,
        max: firstChartMax,
      },
    }
  };

  // Dynamic axis title based on visible datasets
  let axisTitle = '';
  if (visibleDatasets.reps && visibleDatasets.kg) {
    axisTitle = translate('weight') + ' (kg) / ' + translate('reps');
  } else if (visibleDatasets.kg) {
    axisTitle = translate('weight') + ' (kg)';
  } else if (visibleDatasets.reps) {
    axisTitle = translate('reps');
  }

  // KG and Reps Chart data
  const dataChartKg = {
    labels: completeLabels,
    datasets: [
      {
        type: 'bar' as const,
        label: translate('weight'),
        data: displayKgData,
        borderColor: '#dc2626',
        backgroundColor: 'rgba(220, 38, 38, 0.7)',
        borderWidth: 1,
        yAxisID: 'y',
        barPercentage: 0.8,
        categoryPercentage: 0.9,
        datalabels: {
          display: function(context: any) {
            return context.parsed && context.parsed.y !== null && context.parsed.y > 0;
          },
          anchor: 'end' as const,
          align: 'top' as const,
          color: '#000000',
          backgroundColor: 'rgba(252, 229, 226, 0.8)',
          borderColor: '#000000',
          borderRadius: 4,
          borderWidth: 1,
          padding: 2,
          font: { size: 12, weight: 'bold' as const }
        }
      },
      {
        type: 'bar' as const,
        label: translate('reps'),
        data: displayRepsData,
        borderColor: '#13c052',
        backgroundColor: 'rgba(14, 179, 75, 0.7)',
        borderWidth: 1,
        yAxisID: 'y',
        barPercentage: 0.8,
        categoryPercentage: 0.9,
        datalabels: {
          display: function(context: any) {
            return context.parsed && context.parsed.y !== null && context.parsed.y > 0;
          },
          anchor: 'end' as const,
          align: 'top' as const,
          color: '#000000',
          backgroundColor: 'rgba(228, 253, 232, 0.8)',
          borderColor: '#000000',
          borderRadius: 4,
          borderWidth: 1,
          padding: 2,
          font: { size: 12, weight: 'bold' as const }
        }
      }
    ]
  };

  const optionsKg = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: "top" as const,
        onClick: (event: any, legendItem: any, legend: any) => {
          const index = legendItem.datasetIndex;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(index);
          
          // Toggle dataset visibility
          meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
          
          // Update our state to track which datasets are visible
          const newVisibleDatasets = { ...visibleDatasets };
          if (index === 0) { // kg dataset
            newVisibleDatasets.kg = !meta.hidden;
          } else if (index === 1) { // reps dataset
            newVisibleDatasets.reps = !meta.hidden;
          }
          setVisibleDatasets(newVisibleDatasets);
          
          chart.update();
        }
      },
      datalabels: {
        display: false,
      },
      zoom: {
        limits: {
          x: { min: 0, max: completeLabels.length - 1 }
        },
        pan: {
          enabled: true,
          mode: 'x' as const,
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'x' as const,
        }
      }
    },
    scales: {
      x: { 
        title: { display: false, text: translate('date') },
        min: zoomStartIndex,
        max: zoomEndIndex,
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: { display: true, text: axisTitle },
        ticks: { 
          stepSize: visibleDatasets.reps && !visibleDatasets.kg ? 5 : 25,
          callback: function(value: any) {
            return value.toString().padStart(3, ' ');
          }
        },
        min: 0,
        max: kgAxisMax,
      },
    }
  };

  // Show message if user has no machines
  if (userMachinesData && availableMachineTypes.length === 0) {
    return (
      <motion.div 
        className="p-4 mt-6"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        exit={{ opacity: 0, transition: { duration: 0.3 } }}
        style={{ willChange: 'transform' }}
        layoutRoot
      >
        <Flex direction={'column'} align={'center'} justify={'center'} h={300}>
          <Text c={colors.custom36s} fw={500} fz="h4" ta={'center'}>
            {translate('noMachinesAvailable') || 'No machines available for your account'}
          </Text>
        </Flex>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="p-4 mt-6"
      variants={staggerContainer}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
      exit={{ opacity: 0, transition: { duration: 0.3 } }}
      style={{ willChange: 'transform' }}
      layoutRoot
    >
      <Flex direction={'column'} align={'center'}>
        <Flex direction={'row'} gap={2}>
          <div className="overflow-x-hidden">
            <motion.div variants={fadeInFromLeft} layout>
              <Select
                variant="filled"
                radius="xl"
                size='sm'
                fw={500}
                data={groupedMachineTypes}
                value={selectedMachine}
                allowDeselect={false}
                onChange={(value) => setSelectedMachine(value || availableMachineTypes[0]?.value || "")}
                mb="xs"
                maw={185}
                styles={{
                  input: {color: "white", backgroundColor: `${colors.custom36p}dd`, border: 'solid 3px white', fill: 'white'},
                  dropdown: {color: "white", background: colors.custom36p, border: 'solid 3px white', borderRadius: 15, fontWeight: 500},
                  groupLabel: {color: "white"},
                  section: {filter: 'brightness(0) invert(1)'}
                }}
              />
            </motion.div>
          </div>
          <div className="overflow-x-hidden">
            <motion.div variants={fadeInFromRight} layout>
              <Select
                variant="filled"
                radius="xl"
                size='sm'
                fw={500}
                data={timeRangeOptions}
                value={selectedTimeRange}
                allowDeselect={false}
                onChange={(value) => setSelectedTimeRange(value || "1")}
                mb="xs"
                maw={185}
                styles={{
                  input: {color: "white", backgroundColor: `${colors.custom36p}dd`, border: 'solid 3px white', fill: 'white'},
                  dropdown: {color: "white", background: colors.custom36p, border: 'solid 3px white', borderRadius: 15, fontWeight: 500},
                  groupLabel: {color: "white"},
                  section: {filter: 'brightness(0) invert(1)'}
                }}
              />
            </motion.div>
          </div>
        </Flex>
        {/* TYP WYKRESU */}
        {/* <div className="overflow-x-hidden">
          <motion.div variants={fadeInFromTop} layout>
            <SegmentedControl
              radius="xl"
              size="xs"
              color={`${colors.custom36p}dd`}
              value={chartType}
              onChange={(value) => setChartType(value as "line" | "bar")}
              data={[
                { label: (<Flex gap={3} justify={'center'} align={'center'} fz={14}> <IconTimeline size={16}/> <span>{translate('lineChart')}</span> </Flex>), value: "line" },
                { label: (<Flex gap={3} justify={'center'} align={'center'} fz={14}> <IconChartBar size={16}/> <span>{translate('barChart')}</span> </Flex>), value: "bar" },
              ]}
              w={280}
            />
          </motion.div>
        </div> */}
        <motion.div variants={fadeIn} layout>
          <Flex gap={'lg'} direction={'row'} justify={'space-around'} mt={'sm'} pos={'relative'} p={'xs'}>
            <LoadingOverlay
              visible={sumStatsLoading}
              zIndex={2}
              overlayProps={{ radius: 'sm', blur: 2 }}
              loaderProps={{ color: colors.custom36s, type: 'bars' }}
              transitionProps={{ transition: 'fade', duration: 200 }}
              h={'100%'}
              styles={{overlay: {background: 'transparent'}}}
            />
            {isCardio(selectedMachine) ? (
              !dashboardData?.studio.isStudioBasic && (
                <StatItem icon={<IconBike />} value={String(sumStatsData?.sumKm ?? 0)} unit='km' description={translate('distance')} gradient={{ from: 'grape', to: 'indigo', deg: 90 }} />
              )
            ) : (
              <>
                <StatItem icon={<IconBarbellFilled />} value={String(sumStatsData?.sumReps ?? 0)} unit={translate('reps')} description={translate('repetitions')} gradient={{ from: '#248b58', to: '#07aa51', deg: 90 }} />
                <StatItem icon={<IconWeight />} value={String(sumStatsData?.sumKg ?? 0)} unit='kg' description={translate('weight')} gradient={{ from: 'red', to: 'rgba(150, 3, 67, 1)', deg: 90 }} />
              </>
            )}
          </Flex>
        </motion.div>

        {/* Rotation suggestion - only visible in portrait mode on mobile devices */}
        {isPortrait && window.innerWidth < 768 && (
          <motion.div variants={fadeIn}>
          <motion.div 
            layout
            animate={{
              scale: [1, 1.03, 1],
              opacity: [0.7, 0.9, 0.7]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          >
            <Flex direction="column" align="center" justify="center" mt="md" mb="sm">
              <motion.div
                animate={{
                  rotate: [0, 10, -10, 0]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut"
                }}
              >
                <IconDeviceMobileRotated size={20} color={colors.custom36s} />
              </motion.div>
              <Text size="sm" fw={400} c={colors.custom36s} ta="center" style={{ maxWidth: '200px', lineHeight: 1.3 }}>
                {translate('rotateForBetterExperience')}
              </Text>
            </Flex>
          </motion.div>
          </motion.div>
        )}

        <motion.div variants={fadeInFromBottom} layout>
          <Flex h={300} w={'85vw'} mt={'sm'} direction={'column'} ta={'center'} mb={'lg'} pos={'relative'} bg={'#975b0010'} style={{borderRadius: 6, padding: '6px 6px 30px 6px'}}>
            <LoadingOverlay
              visible={statsLoading}
              zIndex={2}
              overlayProps={{ radius: 'sm', blur: 2 }}
              loaderProps={{ color: colors.custom36s, type: 'bars' }}
              transitionProps={{ transition: 'fade', duration: 200 }}
              mt={'xl'}
              h={'100%'}
              styles={{overlay: {background: 'transparent'}}}
            />
            <Text c={colors.custom36s} fw={500} fz="h4" ta={'center'} mb={2}>{translate('loadOverTime')}</Text>
            {chartType==='line' ?
              <>
              <Chart type="line" data={dataChartLine} options={options} />
              </>
            : <>
              <Bar data={dataChartBar} options={options} />
              </>
            }
          </Flex>
        </motion.div>

        {/* KG and Reps Chart - only for non-cardio machines */}
        {!isCardioMachine && (
          <motion.div variants={fadeInFromBottom} layout>
            <Flex h={300} w={'85vw'} mt={'sm'} direction={'column'} ta={'center'} mb={'lg'} pos={'relative'} bg={'#975b0010'} style={{borderRadius: 6, padding: '6px 6px 30px 6px'}}>
              <LoadingOverlay
                visible={statsLoading}
                zIndex={2}
                overlayProps={{ radius: 'sm', blur: 2 }}
                loaderProps={{ color: colors.custom36s, type: 'bars' }}
                transitionProps={{ transition: 'fade', duration: 200 }}
                mt={'xl'}
                h={'100%'}
                styles={{overlay: {background: 'transparent'}}}
              />
              <Text c={colors.custom36s} fw={500} fz="h4" ta={'center'} mb={2}>{translate('weightAndRepsInTime')}</Text>
              <Bar data={dataChartKg} options={optionsKg} plugins={[ChartDataLabels]} />
            </Flex>
          </motion.div>
        )}
      </Flex>
    </motion.div>
  );
};

export default Stats;
